import React, {useState, useEffect, useMemo} from "react";
import axios from "../axios/axios";
import {Box, Stack, List, Center, Button, Text, Input} from "@chakra-ui/react";
import {Link, useSearchParams} from "react-router-dom";
import {Header} from "../components/Header";

import ClientListHStack from "../components/ClientListHStack";

export default function AdminClientsContainer() {
    const [clients, setClients] = useState(null); // null = loading, [] = loaded but empty
    const [searchParams, setSearchParams] = useSearchParams();

    const letter = useMemo(() => searchParams.get("letter") || "A", [searchParams]);
    const search = useMemo(() => searchParams.get("search") || "", [searchParams]);

    const portfolioFilter = useMemo(() => searchParams.get("portfolio") || "Tous", [searchParams]);

    useEffect(() => {
        setClients(null); // Déclenche l'état "loading"

        axios
            .get(`/clients?letter=${letter}&search=${search}`)
            .then(response => {
                setClients(response.data.clients);
            })
            .catch(error => {
                console.error("Error fetching clients:", error);
                setClients([]); // fallback propre
            });
    }, [letter, search]);

    const handleSearch = (event) => {
        const newSearchQuery = event.target.value;

        const params = {};
        if (newSearchQuery !== "") {
            params.search = newSearchQuery;
        } else {
            // Si champ vide, on garde la lettre actuelle
            if (letter) params.letter = letter;
        }

        setSearchParams(params);
    };

    const filteredClients = useMemo(() => {
        if (!clients) return null;
        if (portfolioFilter === "Tous") return clients;
        if (portfolioFilter === "null") {
            return clients.filter(c => !c.portfolio_name_asana);
        }
        return clients.filter(c => c.portfolio_name_asana === portfolioFilter);
    }, [clients, portfolioFilter]);

    const alphabet = ["0-9", ..."ABCDEFGHIJKLMNOPQRSTUVWXYZ"];

    return (
        <Box width={"full"} ml={"20px"}>
            {/* Content for Clients tab */}
            <Header>Clients</Header>
            {/* Search input field */}
            <Box mb={4}>
                <Input placeholder="Recherche par un nom de client" value={search} onChange={handleSearch}/>
            </Box>
            {/* get all the client in a list of button */}
            <Stack direction={"column"} w={"full"}>
                {!search && (
                    <Center margin={"auto"} w={"min-content"} mt={"20px"}>
                        <Stack direction={"row"} spacing={1} mt={6} margin={"auto"} w={"full"}>
                            {alphabet.map(char => {
                                const params = new URLSearchParams(searchParams);
                                params.set("letter", char); // Remplace ou ajoute "letter"
                                return (
                                    <Button
                                        variant={"outline"}
                                        as={Link}
                                        key={char}
                                        to={`/admin/clients?${params.toString()}`}
                                        px={2}
                                        color={char === letter ? "white" : "black"}
                                        background={char === letter ? "blue.900" : "transparent"}
                                        _hover={{bg: "blue.900", color: "white"}}
                                    >
                                        {char}
                                    </Button>
                                );
                            })}

                        </Stack>
                    </Center>
                )}
                <Stack direction="row" spacing={3} mb={4}>
                    {["Tous", "Production", "Archive", "Interne", "null"].map((filter) => (
                        <Button
                            key={filter}
                            size="sm"
                            variant={portfolioFilter === filter ? "solid" : "outline"}
                            colorScheme="blue"
                            onClick={() => {
                                const params = Object.fromEntries([...searchParams.entries()]);
                                if (filter === "Tous") {
                                    delete params.portfolio;
                                } else {
                                    params.portfolio = filter;
                                }
                                setSearchParams(params);
                            }}
                        >
                            {filter === "null" ? "Sans portefeuille" : filter}
                        </Button>
                    ))}
                </Stack>
                <List spacing={3} mt={6}>
                    {filteredClients === null ? (
                        // Affiche 3 skeletons quand ça charge
                        <>
                            <ClientListHStack/>
                            <ClientListHStack/>
                            <ClientListHStack/>
                        </>
                    ) : filteredClients.length > 0 ? (
                        // Résultats trouvés
                        filteredClients.map((client) => (
                            <ClientListHStack
                                key={client.id}
                                client={client}
                                as={Link}
                                to={`/admin/projects?id_client=${client.id}`}
                            />
                        ))
                    ) : (
                        // Aucune donnée
                        <Text color="gray.500" fontStyle="italic">
                            Aucun client trouvé pour cette recherche.
                        </Text>
                    )}
                </List>
            </Stack>
        </Box>
    )
        ;
}
